/**
 * Appearance preference (dark / light / system), shared by every component
 * that reads or sets it.
 *
 * It is a module-level store on purpose. With plain `useState` inside the hook,
 * each caller got its own copy: the brand bar's toggle flipped the <html> class
 * but the hook inside useTenantTheme never heard about it, so the tenant's
 * derived action colour stayed on the previous appearance — light mode kept the
 * dark palette's rose until a reload. One store, many subscribers, no drift.
 *
 * The same preference is read by the boot script in index.html before the
 * first paint — keep STORAGE_KEY and the resolution rules identical in both
 * places, or a reload will flash the other appearance.
 */
import { useCallback, useSyncExternalStore } from "react";

export type ThemeMode = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

const STORAGE_KEY = "wachsen:theme";

function systemPref(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readStored(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "dark" || v === "light" || v === "system") return v;
  } catch {
    // private mode / blocked storage
  }
  return "dark"; // app default — dark, the brand's home ground
}

function applyClass(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const resolved: ResolvedTheme = mode === "system" ? systemPref() : mode;
  root.classList.remove("dark", "light");
  root.classList.add(resolved);
  root.style.colorScheme = resolved;
  // Browser chrome (mobile address bar, PWA) follows the page.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "dark" ? "#0B1A2A" : "#F4F7FB");
}

/* ─── the store ───────────────────────────────────────────────────────── */

let mode: ThemeMode = readStored();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function setModeInternal(next: ThemeMode) {
  if (next === mode) return;
  mode = next;
  applyClass(mode);
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore — the choice just won't survive a reload
  }
  emit();
}

if (typeof window !== "undefined") {
  applyClass(mode);
  // Follow the OS while the user is on "system", and notify subscribers so a
  // derived colour (see useTenantTheme) recomputes with it.
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (mode === "system") {
      applyClass(mode);
      emit();
    }
  });
  // Another tab changing the preference should not leave this one inconsistent.
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      const next = readStored();
      if (next !== mode) {
        mode = next;
        applyClass(mode);
        emit();
      }
    }
  });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
/** Snapshot has to be a stable primitive: the mode plus what it resolves to. */
const getSnapshot = (): string => `${mode}|${mode === "system" ? systemPref() : mode}`;

export function useTheme() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => "light|light");
  const [currentMode, resolved] = snapshot.split("|") as [ThemeMode, ResolvedTheme];

  const setMode = useCallback((next: ThemeMode) => setModeInternal(next), []);
  const toggle = useCallback(() => {
    setModeInternal((mode === "system" ? systemPref() : mode) === "dark" ? "light" : "dark");
  }, []);

  return { mode: currentMode, resolved, setMode, toggle };
}
